import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { ArrowLeftRight, CalendarClock } from 'lucide-react'
import { Link } from 'react-router'
import { EmptyState, ErrorState, FilterBar, PageHeader, StatusBadge } from '@/components/common'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useAllTransfers } from '../api'
import type { TransferStatus } from '../types'
import { Select } from '@/components/ui/select'

const STATUS_LABELS: Record<TransferStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Awaiting approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  RETURNED: 'Returned for changes',
  CANCELLED: 'Cancelled',
  APPLIED: 'Applied',
  REVERTED: 'Reverted',
}

const STATUS_TONE: Record<TransferStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'info',
  REJECTED: 'danger',
  RETURNED: 'warning',
  CANCELLED: 'neutral',
  APPLIED: 'success',
  REVERTED: 'neutral',
}

/**
 * Every transfer in the company, and what is holding each one up.
 *
 * <p>Deliberately a list of what is <em>in flight</em> rather than a history. A transfer that
 * was submitted and then sat unapproved past its effective date is the failure this process
 * has, and it is invisible from the per-employee tab — which only somebody who already
 * suspected a problem would open.
 *
 * <p>Creating and deciding transfers stays where it was, on the employee's Placement tab: a
 * transfer is a change to one person's assignment and needs their current placement in view.
 */
export function TransfersPage() {
  useBreadcrumb()
  const [status, setStatus] = useState<TransferStatus | ''>('')
  const transfers = useAllTransfers(status)

  const today = todayLocal()

  return (
    <div className="space-y-4">
      <PageHeader
        title="Transfers"
        subtitle="Moves between departments, sites and entities — and which are still waiting."
      />

      <FilterBar>
        <Select
          aria-label="Status"
          id="transfer-status"
          value={status}
          onChange={(event) => setStatus(event.target.value as TransferStatus | '')}
        >
          <option value="">Any status</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </Select>
      </FilterBar>

      <Card>
        {transfers.isPending && <Skeleton className="m-4 h-24" />}
        {transfers.isError && (
          <ErrorState error={transfers.error} onRetry={() => void transfers.refetch()} />
        )}
        {transfers.isSuccess && transfers.data.length === 0 && (
          <EmptyState
            title="No transfers"
            description="Transfers are raised from a person's Placement tab, where their current assignment is in view."
          />
        )}
        {transfers.isSuccess && transfers.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-start text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Person</th>
                  <th className="p-3 text-start font-medium">Kind</th>
                  <th className="p-3 text-start font-medium">Moving to</th>
                  <th className="p-3 text-start font-medium">Effective</th>
                  <th className="p-3 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {transfers.data.map((transfer) => {
                  // A submitted transfer whose date has passed is the case worth seeing.
                  const overdue = transfer.status === 'SUBMITTED' && transfer.effectiveFrom < today
                  return (
                    <tr key={transfer.id} className="border-b border-slate-100">
                      <td className="p-3">
                        <Link
                          className="underline underline-offset-2"
                          to={`/employees/${transfer.employeeId}/assignments`}
                        >
                          {transfer.employeeName ?? 'Unknown'}
                        </Link>
                      </td>
                      <td className="p-3 text-slate-600">
                        {transfer.transferType === 'TEMPORARY' ? 'Temporary' : 'Permanent'}
                        {transfer.plannedEndDate && (
                          <span className="block text-xs">until {transfer.plannedEndDate}</span>
                        )}
                      </td>
                      <td className="p-3 text-slate-600">
                        {[transfer.toDepartmentName, transfer.toLocationName, transfer.toDesignationName]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </td>
                      <td className="p-3">
                        {transfer.effectiveFrom}
                        {overdue && (
                          <span className="mt-0.5 flex items-center gap-1 text-xs text-amber-700">
                            <CalendarClock className="size-3.5" aria-hidden="true" />
                            date has passed, still unapproved
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <StatusBadge
                          status={transfer.status}
                          tone={STATUS_TONE[transfer.status]}
                          label={STATUS_LABELS[transfer.status]}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <ArrowLeftRight className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        A transfer is raised and decided on the person&rsquo;s Placement tab, where their
        current assignment and any credential blocks are in view. This screen is for finding
        the ones that have stalled.
      </p>
    </div>
  )
}
